import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Button, CircularProgress, TableCell, TableRow } from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { brandLabel, useBrandNames } from "./brands";
import { downloadCsv } from "./csv";
import {
  DataTable,
  FilterSelect,
  PageHeader,
  StatCard,
  errorMessage,
  fmtPeriod,
  gbp,
  humanize,
  listOf,
  monthOptions,
  useCloStyles,
} from "./shared";

const COLUMNS = [
  { label: "Month" },
  { label: "Retailer" },
  { label: "Trigger" },
  { label: "Count", numeric: true },
  { label: "Amount", numeric: true },
];

const REASONS = ["consent_expired", "account_closure", "inactivity_12m", "admin_clawback"];

const Breakage = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const months = monthOptions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [from, setFrom] = useState(moment().subtract(2, "months").format("YYYY-MM"));
  const [to, setTo] = useState(moment().format("YYYY-MM"));
  const names = useBrandNames(rows.map((r) => r.brand_id));

  const fetchRows = async () => {
    if (from > to) {
      notify("From must be on or before To", "warning");
      return;
    }
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_BREAKAGE, getConfigGet({ from, to }));
      setRows(listOf(res.data));
    } catch (err) {
      notify(errorMessage(err, "Failed to load breakage"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const retailer = (r: any) => (r.brand_id ? brandLabel(names, r.brand_id) : "Whole balance (forfeiture)");
  const totalBy = (reason: string) => rows.filter((r) => r.reason === reason).reduce((sum, r) => sum + Number(r.amount_minor), 0);
  const total = rows.reduce((sum, r) => sum + Number(r.amount_minor), 0);

  const exportCsv = () =>
    downloadCsv(
      `clo-breakage-${from}-to-${to}.csv`,
      ["period", "retailer", "trigger", "count", "amount_gbp"],
      rows.map((r) => ({
        period: r.period,
        retailer: retailer(r),
        trigger: r.reason,
        count: r.count,
        amount_gbp: (Number(r.amount_minor) / 100).toFixed(2),
      }))
    );

  return (
    <div className={classes.main}>
      <PageHeader
        title="Breakage"
        subtitle="Cash back Sprive kept rather than credited: missed through expired bank consent, or forfeited on closure or inactivity. Retailers are still billed."
        actions={
          <Button variant="outlined" onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV
          </Button>
        }
      />

      <div className={classes.filterContainer}>
        <FilterSelect label="From" value={from} onChange={setFrom} options={months} allLabel={null} />
        <FilterSelect label="To" value={to} onChange={setTo} options={months} allLabel={null} />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={fetchRows} disabled={loading}>
          Search
        </Button>
      </div>

      <div className={classes.statRow}>
        <StatCard label="Total" value={gbp(total)} />
        {REASONS.map((reason) => (
          <StatCard key={reason} label={humanize(reason)} value={gbp(totalBy(reason))} />
        ))}
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable columns={COLUMNS} isEmpty={rows.length === 0} empty="No breakage in this range.">
          {rows.map((r) => (
            <TableRow key={`${r.period}-${r.brand_id}-${r.reason}`} hover>
              <TableCell>{fmtPeriod(r.period)}</TableCell>
              <TableCell>{retailer(r)}</TableCell>
              <TableCell>{humanize(r.reason)}</TableCell>
              <TableCell align="right">{r.count}</TableCell>
              <TableCell align="right"><strong>{gbp(r.amount_minor)}</strong></TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <Notification />
    </div>
  );
};

export default Breakage;
